import { ReactNode, useEffect } from 'react';
import api, { isPublicOrCallbackPage } from '../lib/api';
import { useAuthStore } from '../stores/authStore';

interface AuthHydrationGuardProps {
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * Guards rendering until the session check finishes.
 * Ensures we confirm cookie-based auth before making protected requests.
 */
export function AuthHydrationGuard({ children, fallback }: AuthHydrationGuardProps) {
  const hasHydrated = useAuthStore((state) => state._hasHydrated);
  const login = useAuthStore((state) => state.login);
  const logout = useAuthStore((state) => state.logout);
  const setHasHydrated = useAuthStore((state) => state.setHasHydrated);

  useEffect(() => {
    let isMounted = true;

    const bootstrap = async () => {
      try {
        if (isPublicOrCallbackPage()) {
          if (!isMounted) return;
          setHasHydrated(true);
          return;
        }

        const response = await api.get('/auth/me');
        if (!isMounted) return;
        login(response.data.user, response.data.tenantContext ?? null);
      } catch {
        if (!isMounted) return;
        if (!isPublicOrCallbackPage()) {
          logout();
        }
      } finally {
        if (!isMounted) return;
        setHasHydrated(true);
      }
    };

    if (!hasHydrated) {
      bootstrap();
    }

    return () => {
      isMounted = false;
    };
  }, [hasHydrated, login, logout, setHasHydrated]);

  if (!hasHydrated) {
    return fallback ?? (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-slate-400">Loading...</div>
      </div>
    );
  }

  return children;
}
